import { Injectable } from '@angular/core';
import { defer, finalize, Observable, shareReplay, Subject, throwError } from 'rxjs';
import { ErrorCodes } from '../../api/v1/models/error-codes';
import { ResponseError } from '../../api/v1/models/response-error';
import { ErrorMessages } from './error-messages';
import { StatusMessageService } from '../../shared/services/status-message.service';

interface ErrorConfig {
  subject: Subject<string>,
  isEmitModal: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerGlobalService {
  constructor(
    private messageService: StatusMessageService
  ) {}

  private unknownErrorMessage: string = $localize`:@@OrderDefaultError:Oops... It seems something is broken. We are already working on fixing the problem. We will be happy to accept your order by %phone%. Thank you for choosing our restaurant!`

  private errorSubjects = new Map<ErrorCodes | 'UNKNOWN_ERROR', ErrorConfig>();

  handleError(errorKey: ErrorCodes | 'UNKNOWN_ERROR', error: ResponseError | null = null): Observable<never> {
    this.emitError(errorKey);
    return throwError(() => error);
  }

  getError$(errorKey: ErrorCodes, isEmitModal: boolean = true): Observable<string> {
    return defer(() => {
      const errorConfig = this.errorSubjects.get(errorKey) ?? {
        subject: new Subject<string>(),
        isEmitModal: isEmitModal,
      };

      if (!this.errorSubjects.has(errorKey)) {
        this.errorSubjects.set(errorKey, errorConfig);
      }

      if(!isEmitModal){
        errorConfig.isEmitModal = false;
      }

      return errorConfig.subject.asObservable();
    }).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        const errorConfig = this.errorSubjects.get(errorKey);
        if(!errorConfig){return}

        if(!isEmitModal){
          errorConfig.isEmitModal = true;
        }

        if(!errorConfig.subject.observed){
          this.errorSubjects.delete(errorKey);
        }
      })
    );
  }

  private emitError(errorKey: ErrorCodes | 'UNKNOWN_ERROR'): void {
    const errorConfig = this.errorSubjects.get(errorKey);
    const message = ErrorMessages[errorKey] || this.unknownErrorMessage;

    if(!errorConfig || errorConfig.isEmitModal){
      this.messageService.setStatusMessage({
        title: $localize`:@@MessageModalTitleError:Error`,
        message: message,
        type: 'error'
      });
    }

    errorConfig?.subject.next(message);
  }
}
